import { useEffect } from 'react';

type NavigationGuard = (url: string) => boolean;

const guards = new Set<NavigationGuard>();

/**
 * Registers a navigation guard for the lifetime of the calling component.
 * Guards are checked before in-app navigation (see `guardNavigate`) and may
 * veto it (e.g. to show an unsaved-changes dialog).
 */
export const useNavigationGuard = (guard: NavigationGuard) => {
    useEffect(() => {
        guards.add(guard);
        return () => {
            guards.delete(guard);
        };
    }, [guard]);
};

/**
 * Runs all registered navigation guards for the given target URL.
 * Returns true when navigation is allowed, false when a guard vetoed it.
 */
export const isNavigationAllowed = (url: string): boolean => {
    for (const guard of guards) {
        if (!guard(url)) {
            return false;
        }
    }
    return true;
};

/**
 * Navigates to `url` unless a registered guard vetoes the navigation.
 * External URLs (http/https) open in a new tab.
 */
export const guardNavigate = (url: string): boolean => {
    if (!isNavigationAllowed(url)) {
        return false;
    }

    if (/^https?:\/\//i.test(url)) {
        window.open(url, '_blank');
        return true;
    }

    window.location.href = url;
    return true;
};
